import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  IMAGES_DEFAULT_HEIGHT,
  IMAGES_DEFAULT_WIDTH,
} from '../sessions/session/iframe/additional-apps.utils';
import { CdnResourcesSignerService } from './cdn-resources-signer.service';
import { FLAGS, FlagsService } from './flags.service';
import { pollHeadUntilUrlOk } from './url-head-poll';

export const DEFAULT_HEIC_CONVERSION_RELOAD_ATTEMPTS = 25;
export const DEFAULT_HEIC_CONVERSION_POLL_INTERVAL_MS = 2000;

/**
 * Cloud HEIC/HEIF → WebP path: converted object URL, readiness polling, and layout helpers
 * after the WebP is available (same bucket layout as `pencil-heic-to-webp`).
 */
@Injectable({
  providedIn: 'root',
})
export class ImageConversionService {
  constructor(
    private readonly flagsService: FlagsService,
    private readonly cdnResourcesSignerService: CdnResourcesSignerService,
  ) {}

  /** URL for the WebP at `converted/{base}.webp` (same base rule as `webpPathForObject`). */
  async buildConvertedWebpUrl(
    originalDownloadUrl: string,
    rootObjectName: string,
  ): Promise<string> {
    const dot = rootObjectName.lastIndexOf('.');
    const base = dot > 0 ? rootObjectName.slice(0, dot) : rootObjectName;
    const convertedPath = `converted/${base}.webp`;

    if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_CDN_RESOURCES_SIGNING)) {
      try {
        return await this.cdnResourcesSignerService.getSignedUrl(convertedPath);
      } catch {
        // Fall through to Firebase pattern URL below
      }
    }

    const pattern = (environment as { convertedHeicWebpUrlPattern?: string })
      .convertedHeicWebpUrlPattern;
    if (!pattern) {
      return originalDownloadUrl;
    }

    const urlObj = new URL(originalDownloadUrl);
    const token = urlObj.searchParams.get('token') ?? '';
    const bucket = environment.firebase.storageBucket;

    return pattern.replace('{bucket}', bucket).replace('{base}', base).replace('{token}', token);
  }

  async waitForConvertedWebp(url: string): Promise<boolean> {
    const maxAttempts = Number(
      this.flagsService.featureFlagsVariables[FLAGS.ENABLE_IMAGES_CONVERSION]
        ?.max_conversion_reload_attempts ?? DEFAULT_HEIC_CONVERSION_RELOAD_ATTEMPTS,
    );
    const intervalMs = Number(
      this.flagsService.featureFlagsVariables[FLAGS.ENABLE_IMAGES_CONVERSION]
        ?.conversion_poll_interval_ms ?? DEFAULT_HEIC_CONVERSION_POLL_INTERVAL_MS,
    );
    return pollHeadUntilUrlOk(url, maxAttempts, intervalMs);
  }

  transformDimensionsToTargetAreaPreservingAspectRatio(
    intrinsicWidth: number,
    intrinsicHeight: number,
    targetArea: number,
  ): { height: number; width: number } {
    if (intrinsicWidth <= 0 || intrinsicHeight <= 0 || targetArea <= 0) {
      return { width: IMAGES_DEFAULT_WIDTH, height: IMAGES_DEFAULT_HEIGHT };
    }
    const aspectRatio = intrinsicWidth / intrinsicHeight;
    const height = Math.sqrt(targetArea / aspectRatio);
    const width = aspectRatio * height;
    return {
      width: Math.max(1, Math.round(width)),
      height: Math.max(1, Math.round(height)),
    };
  }
}
